import type { CourtType, Play, PlayStep, Player, Position } from "../types";

export const PLAYER_COLOR_PALETTE = [
  { value: "#D8F26A", label: "青柠" },
  { value: "#6ED3FF", label: "天蓝" },
  { value: "#FFD166", label: "金黄" },
  { value: "#FF8A80", label: "珊瑚" },
  { value: "#C5A3FF", label: "淡紫" },
  { value: "#62E6C5", label: "薄荷" },
  { value: "#FFAD66", label: "橙色" },
  { value: "#F4F1E8", label: "米白" },
] as const;

export function limitPlayerName(value: string) {
  return Array.from(value).slice(0, 2).join("");
}

export function playerLabel(player: Player) {
  return player.name?.trim() || String(player.number);
}

const offensePositions: Position[] = [
  { x: 50, y: 64 },
  { x: 18, y: 50 },
  { x: 82, y: 50 },
  { x: 34, y: 28 },
  { x: 66, y: 28 },
];

const defensePositions: Position[] = [
  { x: 50, y: 54 },
  { x: 24, y: 43 },
  { x: 76, y: 43 },
  { x: 38, y: 23 },
  { x: 62, y: 23 },
];

export function createInitialStep(): PlayStep {
  const players: Player[] = [
    ...offensePositions.map((position, index) => ({
      ...position,
      id: `o${index + 1}`,
      number: index + 1,
      side: "offense" as const,
      visible: true,
      color: PLAYER_COLOR_PALETTE[index].value,
    })),
    ...defensePositions.map((position, index) => ({
      ...position,
      id: `d${index + 1}`,
      number: index + 1,
      side: "defense" as const,
      visible: true,
    })),
  ];

  return {
    id: crypto.randomUUID(),
    title: "初始站位",
    note: "",
    duration: 1.5,
    ballOwnerId: "o1",
    ball: { x: 50, y: 64 },
    players,
    markers: [],
  };
}

export function createPlay(courtType: CourtType = "half"): Play {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    name: "未命名战术",
    description: "",
    courtType,
    steps: [createInitialStep()],
    version: 0,
    createdAt: now,
    updatedAt: now,
  };
}

export function cloneStep(step: PlayStep): PlayStep {
  return {
    ...structuredClone(step),
    id: crypto.randomUUID(),
    title: `第 ${Number(step.title.match(/\d+/)?.[0] ?? 1) + 1} 步`,
    markers: [],
  };
}

export function clonePlay(play: Play): Play {
  const now = new Date().toISOString();
  return {
    ...structuredClone(play),
    id: crypto.randomUUID(),
    name: `${play.name}（副本）`,
    version: 0,
    createdAt: now,
    updatedAt: now,
    steps: play.steps.map((step) => ({ ...step, id: crypto.randomUUID() })),
  };
}

export function ownerBallPosition(step: PlayStep): Position {
  const owner = step.players.find((player) => player.id === step.ballOwnerId);
  return owner ? { x: owner.x + 2.3, y: owner.y + 1.8 } : step.ball;
}

function mix(from: number, to: number, progress: number) {
  return from + (to - from) * progress;
}

function mixPosition(from: Position, to: Position, progress: number): Position {
  return { x: mix(from.x, to.x, progress), y: mix(from.y, to.y, progress) };
}

export function interpolateStep(from: PlayStep, to: PlayStep, progress: number): PlayStep {
  if (progress <= 0) return from;
  if (progress >= 1) return to;
  const nextById = new Map(to.players.map((player) => [player.id, player]));
  return {
    ...from,
    players: from.players.map((player) => {
      const target = nextById.get(player.id) ?? player;
      return { ...player, ...mixPosition(player, target, progress), visible: player.visible || target.visible };
    }),
    ballOwnerId: null,
    ball: mixPosition(ownerBallPosition(from), ownerBallPosition(to), progress),
    markers: progress < 0.5 ? from.markers : to.markers,
  };
}

export function updatePlayerProfile(play: Play, playerId: string, profile: Pick<Player, "name" | "color">): Play {
  return {
    ...play,
    steps: play.steps.map((step) => ({
      ...step,
      players: step.players.map((player) => player.id === playerId ? { ...player, ...profile } : player),
    })),
  };
}

export function sanitizePlayForSave(play: Play): Play {
  return {
    ...play,
    name: play.name.trim() || "未命名战术",
    description: play.description.trim(),
    updatedAt: new Date().toISOString(),
    steps: play.steps.map((step, index) => ({
      ...step,
      title: step.title.trim() || `第 ${index + 1} 步`,
      duration: Math.min(10, Math.max(0.3, Number(step.duration) || 1.5)),
      ball: ownerBallPosition(step),
      players: step.players.map((player) => {
        const name = limitPlayerName(player.name?.trim() ?? "");
        return { ...player, name: name || undefined };
      }),
    })),
  };
}
